import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'

import type { UseSubscriberPayload } from '@hooks/useSubscriber'
import useSubscriber from '@hooks/useSubscriber'
import useSubscribers from '@hooks/useSubscribers'

import type { SubscriberModel, UserModel } from '@models/types'

import Subscriber from '@components/Subscriber'

const MODAL_KEY = 'ManageSubscribersModal'

interface HandleCreateSubscriberParams {
  createFn: () => UseSubscriberPayload['callbacks']['createSubscriber'],
  createSubscriber: (payload: HandleCallbackActionParams) => void,
  initSubscriber: SubscriberModel,
  user: UserModel,
}

const handleCreateSubscriber = (params: HandleCreateSubscriberParams) => {
  const {
    createFn, createSubscriber, initSubscriber, user,
  } = params

  const { subscribable_id, subscribable_type } = initSubscriber

  const createSubscriberPayload = {
    callbacks: {
      action: createFn,
    },
    entityParams: { user_id: user.id, subscribable_type, subscribable_id },
    toastText: 'Subscriber Added',
  }

  // @ts-expect-error TS2345
  createSubscriber(createSubscriberPayload)
}

type ModalCallbacks = {
  closeModal: () => void,
  createSubscriber: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageSubscribersModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createSubscriber } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { subscriber: initSubscriber } = modalPayload

  const subscribersPayload = useSubscribers({
    filters: {
      subscribable_id: initSubscriber.subscribable_id,
      subscribable_type: initSubscriber.subscribable_type,
    },
  })

  const { filteredSubscribers, filteredSubscribersCount, hasFilteredSubscribers } = subscribersPayload

  const {
    callbacks: {
      createSubscriber: createFn,
    },
  } = useSubscriber({})

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Subscribers" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              // @ts-expect-error TS2322
              selectItem: (user: UserModel) => {
                handleCreateSubscriber({
                  // @ts-expect-error TS2322
                  createSubscriber, createFn, user, initSubscriber,
                })
              },
            }}
            entityKey="users"
            label="* User"
            requestOptions={{
              include_image_sizes: true,
              include_user_image: true,
            }}
            selectedIds={filteredSubscribers.map(sub => sub.user_id)}
            showSelectedEntity={false}
          />
        </Form>

        {hasFilteredSubscribers && (
          <Form.Field direction="column" label="Selected Subscribers" marginTop="large">
            {filteredSubscribers.map(subscriber => (
              <Subscriber
                filteredSubscribersCount={filteredSubscribersCount}
                initSubscriber={subscriber}
                key={subscriber.id}
              />
            ))}
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageSubscribersModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
